'use client';
import { useEffect, useLayoutEffect, useState } from 'react';
import type { SiteBanner } from '../lib/site';
import { useT } from '../lib/i18n';
import { useLayerLock } from '../lib/useLayerLock';
import { BnSlider } from './BnSlider';
import { Icon } from './Icon';

/**
 * 인덱스 페이지의 배너 위치들 — 현행 index.php 의 5자리를 가이드 benner-guide 규약으로 옮겼다.
 *  · line   띠배너(메인 위 풀와이드) — 1장이면 단독 띠(배경 그라데이션), 여러 장이면 슬라이더
 *  · top / bottom  상·하단 플로팅 바 — "오늘은 그만보기"(780px 이하는 CSS 가 숨긴다)
 *  · left / right  사이드 배너 — 항목별 닫기(1540px 이하는 CSS 가 숨긴다)
 * 노출 판정(사용·기간)은 서버가 끝냈다 — 여기는 그리기와 닫기 기억뿐이다.
 */
export function Img({ b }: { b: SiteBanner }) {
  /*
   * PC·모바일 이미지는 가이드 공통 규약(bn-img-pc/bn-img-mo — 768px 경계 CSS)으로 가른다.
   * 크기·배치는 각 배너 자리의 가이드 CSS 가 정한다(.bn-slide img·.bn-side-item img …) —
   * 여기서 인라인 스타일을 얹지 않는다.
   */
  const img = b.imagePc && b.imageMobile
    ? <>
        <img className="bn-img-pc" src={b.imagePc} alt={b.title} />
        <img className="bn-img-mo" src={b.imageMobile} alt={b.title} />
      </>
    : <img src={(b.imagePc ?? b.imageMobile)!} alt={b.title} />;
  if (!b.link) return <span style={{ display: 'block', lineHeight: 0 }}>{img}</span>;
  return <a href={b.link} target={b.openNewWindow ? '_blank' : undefined} rel="noreferrer">{img}</a>;
}

/**
 * 레이아웃 이펙트 — 브라우저가 그리기 **전에** 돈다. 서버에는 없으므로 갈라 쓴다
 * (`useLayoutEffect` 를 SSR 에서 부르면 React 가 경고한다).
 */
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * 오늘 하루 닫기 — 자정(달력 날짜)이 바뀌면 다시 뜬다. 팝업·플로팅 바 공용.
 *
 * **안 보이는 것이 기본이다**(2026-08-22 사용자 지적). 전에는 SSR 이 배너를 그려 놓고
 * 마운트 뒤 localStorage 를 읽어 지웠다 — 새로고침마다 닫아 둔 배너가 **한 번 번쩍**였다.
 * 그래서 `ready` 가 되기 전에는 아무것도 그리지 않고, 판정이 끝난 뒤에만 보여 준다.
 * 판정은 `useLayoutEffect`(그리기 전)에서 하므로 보여 줄 배너도 빈 화면을 스치지 않는다.
 *
 * 첫 렌더가 서버·클라 모두 '없음' 이라 hydration 도 어긋나지 않는다.
 */
function useTodayClosed(storeKey: string) {
  const today = new Date().toDateString();
  const [closed, setClosed] = useState<Set<number>>(new Set());
  const [ready, setReady] = useState(false);
  useBeforePaint(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storeKey) ?? '{}') as { day?: string; ids?: number[] };
      if (saved.day === today && saved.ids?.length) setClosed(new Set(saved.ids));
    } catch { /* 손상된 저장값·접근 차단 — 없는 것으로 본다 */ }
    setReady(true);
  }, [storeKey, today]);
  const close = (ids: number[], skipToday: boolean) => {
    const next = new Set(closed);
    for (const id of ids) next.add(id);
    setClosed(next);
    if (skipToday) {
      try {
        localStorage.setItem(storeKey, JSON.stringify({ day: today, ids: [...next] }));
      } catch { /* 저장이 안 되더라도 이번 방문에는 닫혀 있어야 한다 */ }
    }
  };
  return { closed, close, ready };
}

/** 띠배너(line) — 1장 + 배경색이면 현행 Top_Bnn(좌우 50/50 그라데이션 띠), 여러 장이면 풀와이드 슬라이더 */
export function LineBanner({ banners }: { banners: SiteBanner[] }) {
  const rows = banners.filter((b) => b.imagePc || b.imageMobile);
  if (!rows.length) return null;
  if (rows.length === 1) {
    const b = rows[0];
    return (
      <div className="bn-fullwide" style={b.bgColorEnabled && b.bgColorLeft && b.bgColorRight
        ? { background: `linear-gradient(to right, ${b.bgColorLeft} 50%, ${b.bgColorRight} 50%)`, textAlign: 'center' }
        : undefined}>
        {/* 띠 배경 위에 원본 폭 그대로 — 늘리면 그라데이션 배경을 쓰는 뜻이 없다 */}
        {b.link
          ? <a href={b.link} target={b.openNewWindow ? '_blank' : undefined} rel="noreferrer"><img src={(b.imagePc ?? b.imageMobile)!} alt={b.title} style={{ maxWidth: '100%' }} /></a>
          : <img src={(b.imagePc ?? b.imageMobile)!} alt={b.title} style={{ maxWidth: '100%' }} />}
      </div>
    );
  }
  return (
    <BnSlider className="bn-fullwide">
      {rows.map((b) => <Img key={b.id} b={b} />)}
    </BnSlider>
  );
}

/**
 * 상·하단 플로팅 바 — 오늘 그만보기. 여러 장이면 슬라이더로 돌린다(바를 겹쳐 쌓지 않는다).
 *
 * **상단과 하단의 처리가 다르다** — CSS 가 다르기 때문이다(2026-08-22).
 *  · 하단은 `position: fixed` 라 늦게 나타나도 화면이 밀리지 않는다 → 판정 뒤에만 그린다.
 *  · 상단은 헤더와 한 덩어리(`.bn-top-sticky-wrap`)로 **문서 흐름에 있다.** 늦게 나타나면
 *    헤더와 본문이 아래로 밀린다. 그래서 서버가 그대로 그리고, 닫아 둔 방문자에게는
 *    `<head>` 의 인라인 스크립트가 **첫 페인트 전에** 숨긴다(`html[data-bn-top=off]`).
 *    그러면 번쩍임도 밀림도 없다.
 */
export function FloatBar({ banners, position }: { banners: SiteBanner[]; position: 'top' | 'bottom' }) {
  const t = useT();
  const { closed, close, ready } = useTodayClosed(`floatbar_${position}`);
  const rows = banners.filter((b) => !closed.has(b.id) && (b.imagePc || b.imageMobile));
  if (position === 'bottom' && !ready) return null;
  if (!rows.length) return null;
  const ids = rows.map((b) => b.id);
  return (
    <div className={`bn-float-bar ${position}`}>
      <div className="bn-float-bar-inner">
        {rows.length === 1
          ? <span className="bn-float-bar-link"><Img b={rows[0]} /></span>
          : <span className="bn-float-bar-link"><BnSlider auto>{rows.map((b) => <Img key={b.id} b={b} />)}</BnSlider></span>}
        <div className="bn-float-bar-actions">
          <button className="bn-float-bar-today" onClick={() => close(ids, true)}>{t('banner.skipToday')}</button>
          <button className="bn-float-bar-close" onClick={() => close(ids, false)} aria-label={t('notice.close')}>
            <Icon name="x" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 사이드 배너(left/right) — 항목별 닫기(세션 동안만 — 현행도 새로고침이면 다시 뜬다).
 *
 * **세로 기준은 헤더다**(2026-08-27 사용자 확정 — "화면 가운데는 신경 거슬린다").
 * 가이드가 가운데(top 50%)로 둔 것은 CSS 만으로는 기준을 못 잡아서다 — 상단이
 * [탑배너+헤더] 한 덩어리 sticky 인데 그 높이가 가변이다(배너 유무·닫기·반응형).
 * 그래서 그 덩어리 높이를 **실측**해 바로 아래 20px 에 붙인다(ResizeObserver 라
 * 탑배너를 닫으면 그만큼 따라 올라온다). 덩어리가 없으면 가이드 기본(가운데)로 둔다.
 */
export function SideBanners({ banners, side }: { banners: SiteBanner[]; side: 'left' | 'right' }) {
  const [closed, setClosed] = useState<Set<number>>(new Set());
  const [top, setTop] = useState<number | null>(null);
  useEffect(() => {
    const head = document.querySelector('.bn-top-sticky-wrap');
    if (!head) return;
    const sync = () => setTop(head.getBoundingClientRect().height + 20);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(head);
    return () => ro.disconnect();
  }, []);
  const rows = banners.filter((b) => !closed.has(b.id) && (b.imagePc || b.imageMobile));
  if (!rows.length) return null;
  return (
    <div className={`bn-side-wrap ${side}`}
      style={top != null ? { top, transform: 'none' } : undefined}>
      {rows.map((b) => (
        <div className="bn-side-item" key={b.id}>
          <Img b={b} />
          <button className="bn-side-close" onClick={() => setClosed((s) => new Set(s).add(b.id))} aria-label="close">
            <Icon name="x" />
          </button>
        </div>
      ))}
    </div>
  );
}

/** 팝업 — 가이드 bn-popup 규약(슬라이더 내장·오늘 하루 체크). 등록된 창 크기(popup_width)를 따른다 */
export function PopupBanners({ banners }: { banners: SiteBanner[] }) {
  const t = useT();
  const { closed, close, ready } = useTodayClosed('popup_closed');
  const [skip, setSkip] = useState(false);
  const rows = banners.filter((b) => !closed.has(b.id) && (b.imagePc || b.imageMobile));
  // 스크롤 잠금 + Esc 닫기(훅은 조건 앞에서 불러야 한다). 딤 클릭으로는 닫지 않는다
  useLayerLock(ready && rows.length > 0, () => close(rows.map((b) => b.id), skip));
  if (!ready || !rows.length) return null;

  const width = rows.find((b) => b.popupWidth)?.popupWidth;
  return (
    <>
      {/* 딤 클릭으로는 닫지 않는다(2026-08-22) — "오늘 하루" 체크를 누르려다 딤을 스치면 닫혔다.
          닫기는 X·닫기 버튼과 Esc */}
      <div className="bn-popup-backdrop on"></div>
      <div className="bn-popup on">
        <div className="bn-popup-inner" style={width ? { maxWidth: Number(width) || undefined } : undefined}>
          <BnSlider auto>{rows.map((b) => <Img key={b.id} b={b} />)}</BnSlider>
          <button className="bn-popup-close" onClick={() => close(rows.map((b) => b.id), skip)} aria-label={t('notice.close')}>
            <Icon name="x" />
          </button>
          <div className="bn-popup-foot">
            <label className="bn-popup-today">
              <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} /> {t('banner.skipToday')}
            </label>
            <button className="bn-popup-close-txt" onClick={() => close(rows.map((b) => b.id), skip)}>{t('notice.close')}</button>
          </div>
        </div>
      </div>
    </>
  );
}
